// src/hooks/useManufacturing.ts
import { useState, useEffect } from 'react';
import { message } from 'antd';
import { manufacturingApi, ManufacturingLog, ManufacturingStats, StockStatus } from '@/services/api/manufacturing';

interface ManufacturingParams {
  page?: number;
  limit?: number;
  menu_item?: number;
  menu_item__category?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
  ordering?: string;
}

export const useManufacturing = () => {
  const [logs, setLogs] = useState<ManufacturingLog[]>([]);
  const [stats, setStats] = useState<ManufacturingStats | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    has_next: false,
    has_prev: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Загрузка списка логов производства
  const loadLogs = async (params?: ManufacturingParams) => {
    setLoading(true);
    try {
      console.log('📦 Загрузка логов производства с параметрами:', params);
      const response = await manufacturingApi.getLogs(params);
      console.log('✅ Ответ от API:', response.data);
      const data = response.data.data;
      
      setLogs(data.items);
      setPagination(data.pagination);
    } catch (error: any) {
      console.error('❌ Ошибка загрузки логов:', error);
      const errorMsg = error.response?.data?.message || 'Ошибка загрузки истории производства';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Загрузка статистики
  const loadStats = async (days: number = 7) => {
    try {
      console.log(`📊 Загрузка статистики за ${days} дней`);
      const response = await manufacturingApi.getStats({ days });
      console.log('✅ Статистика получена:', response.data);
      setStats(response.data.data);
    } catch (error: any) {
      console.error('❌ Ошибка загрузки статистики:', error);
      const errorMsg = error.response?.data?.message || 'Ошибка загрузки статистики';
      message.error(errorMsg);
    }
  };

  // Создание записи о производстве
  const createLog = async (data: {
    menu_item: number;
    quantity_added: number;
    comment?: string;
  }) => {
    setActionLoading(true);
    try {
      console.log('➕ Создание записи о производстве:', data);
      const response = await manufacturingApi.createLog(data);
      console.log('✅ Запись создана:', response.data);
      message.success('Запись о производстве успешно добавлена');
      
      // Обновляем список
      await loadLogs();
      await loadStats();
      
      return response.data.data;
    } catch (error: any) {
      console.error('❌ Ошибка создания записи:', error);
      const errorMsg = error.response?.data?.message || 'Ошибка создания записи о производстве';
      message.error(errorMsg);
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Удаление записи
  const deleteLog = async (logId: number) => {
    setActionLoading(true);
    try {
      console.log('🗑️ Удаление записи:', logId);
      await manufacturingApi.deleteLog(logId);
      message.success('Запись успешно удалена');
      
      // Обновляем список
      await loadLogs();
      await loadStats();
    } catch (error: any) {
      console.error('❌ Ошибка удаления записи:', error);
      const errorMsg = error.response?.data?.message || 'Ошибка удаления записи';
      message.error(errorMsg);
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Получение статуса запасов конкретного блюда
  const getStockStatus = async (menuItemId: number): Promise<StockStatus | null> => {
    try {
      console.log('📦 Получение статуса запасов блюда:', menuItemId);
      const response = await manufacturingApi.getStockStatus(menuItemId);
      console.log('✅ Статус получен:', response.data);
      return response.data.data;
    } catch (error: any) {
      console.error('❌ Ошибка получения статуса:', error);
      const errorMsg = error.response?.data?.message || 'Ошибка получения статуса запасов';
      message.error(errorMsg);
      return null;
    }
  };

  // Перезагрузка текущих данных
  const refresh = () => {
    loadLogs({
      page: Math.floor(pagination.offset / pagination.limit) + 1,
      limit: pagination.limit,
    });
    loadStats();
  };

  // Загрузка базовых данных при монтировании
  useEffect(() => {
    loadLogs();
    loadStats();
  }, []);

  return {
    // Данные
    logs,
    stats,
    pagination,
    
    // Состояние загрузки
    loading,
    actionLoading,
    
    // Методы
    loadLogs,
    loadStats,
    createLog,
    deleteLog,
    getStockStatus,
    refresh,
  };
};